import React, { useMemo, useCallback, useEffect, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

import {
  SortingRule,
  IdType,
  useGlobalFilter,
  useFilters,
  Row,
} from "react-table";
import {
  ActionGeneric,
  HvBulkActions,
  HvEmptyState,
  HvPagination,
  ListValueProp,
} from "@hitachivantara/uikit-react-core";
import {
  useHvBulkActions,
  useHvPagination,
  useHvRowSelection,
  useHvTable,
  HvTable,
  HvTableBody,
  HvTableCell,
  HvTableContainer,
  HvTableHead,
  HvTableHeader,
  HvTableRow,
  HvTableOptions,
  HvTableColumnConfig,
  HvRowInstance,
  useHvSortBy,
  HvTableInstance,
} from "@hitachivantara/uikit-react-lab";
import { Ban } from "@hitachivantara/uikit-react-icons";
import OverflowTooltip from "components/common/OverflowTooltip";
import LoadingContainer from "components/layout/LoadingContainer";
import { nodeFallback } from "lib/utils/fallbacks";

import useStyles from "./styles";

interface SimpleTableProps<T extends Record<string, unknown>>
  extends HvTableOptions<T> {
  columns?: Array<HvTableColumnConfig<T>>;
  instance?: HvTableInstance<T>;
  rowId: keyof T;
  totalCount?: number;
  bulkActions?: ActionGeneric[];
  onSelectAll?: (
    event: React.MouseEvent<Element, MouseEvent>,
    rows: HvRowInstance<MemberDataRow>[],
    isAllPageRowsSelected: boolean
  ) => void;
  onSelectAllPages?: (
    event: ChangeEvent<HTMLInputElement>,
    rows: HvRowInstance<MemberDataRow>[],
    toggleAllRowsSelected?: (value?: boolean | undefined) => void
  ) => void;
  setPage?: React.Dispatch<React.SetStateAction<number>>;
  setSize?: React.Dispatch<React.SetStateAction<number>>;
  setSortBy?: React.Dispatch<
    React.SetStateAction<SortingRule<MemberDataRow>[]>
  >;
  setSelectedRowIds?: React.Dispatch<
    React.SetStateAction<Record<IdType<MemberDataRow>, boolean>>
  >;
  loading?: boolean;
  onChangeRow?: (
    event: React.FormEvent<HTMLTableRowElement> | undefined,
    row: HvRowInstance<MemberDataRow>
  ) => void;
  onGlobalFilter?: (
    rows: Row<MemberDataRow>[],
    columnIds: IdType<MemberDataRow>[],
    filterValue: MembersTableFilter
  ) => Row<MemberDataRow>[];
  groupsKeys?: ListValueProp[];
  globalFilterValues: MembersTableFilter;
  searchKey?: string;
  viewOption?: "all" | "member";
}

export const DEFAULT_PAGE_SIZE = 10;

const UserTable = ({
  rowId,
  totalCount = 0,
  bulkActions,
  columns,
  initialState,
  labels,
  data,
  groupsKeys,
  globalFilterValues,
  setPage,
  setSize,
  setSortBy,
  setSelectedRowIds,
  onChangeRow,
  onGlobalFilter,
  onSelectAll,
  onSelectAllPages,
  loading = false,
}: SimpleTableProps<MemberDataRow>): JSX.Element => {
  const classes = useStyles();

  const { t } = useTranslation("common");

  const paginationLabels = useMemo(
    () => ({
      pageSizePrev: t("simple_table.pageSizePrefixLabel"),
      pageSizeEntryName: t("simple_table.pageSizeSuffixLabel"),
      pageSizeSelectorDescription: t("simple_table.pageSizeSelectorAriaLabel"),
      pagesSeparator: t("simple_table.pagesSeparator"),
      paginationFirstPageTitle: t("simple_table.firstPageButtonTooltip"),
      paginationPreviousPageTitle: t("simple_table.previousPageButtonTooltip"),
      paginationNextPageTitle: t("simple_table.nextPageButtonTooltip"),
      paginationLastPageTitle: t("simple_table.lastPageButtonTooltip"),
      paginationInputLabel: t("simple_table.currentPageInputAriaLabel"),
      firstPage: t("simple_table.firstPageButtonAriaLabel"),
      previousPage: t("simple_table.previousPageButtonAriaLabel"),
      nextPage: t("simple_table.nextPageButtonAriaLabel"),
      lastPage: t("simple_table.lastPageButtonAriaLabel"),
    }),
    [t]
  );

  const getRowId = useCallback(
    (v: MemberDataRow): string => `${v[rowId]}`,
    [rowId]
  );

  const plugins = [];
  plugins.push(useGlobalFilter);
  plugins.push(useFilters);
  plugins.push(useHvSortBy);
  plugins.push(useHvPagination);
  plugins.push(useHvRowSelection);
  plugins.push(useHvBulkActions);

  const {
    getTableProps,
    getTableBodyProps,
    prepareRow,
    headers,
    rows,
    page,
    state: { pageIndex, pageSize, selectedRowIds, sortBy },
    getHvBulkActionsProps,
    getHvPaginationProps,
    setGlobalFilter,
    setFilter,
    toggleAllRowsSelected,
    isAllPageRowsSelected,
  } = useHvTable<MemberDataRow>(
    {
      columns,
      data,
      getRowId,
      pageCount: Math.ceil((totalCount ?? 0) / (initialState?.pageSize ?? 1)),
      autoResetSelectedRows: false,
      initialState,
      manualRowSelectedKey: "isSelected",
      labels,
      globalFilter: onGlobalFilter,
      aditivePageBulkSelection: true,
      subtractivePageBulkDeselection: true,
      disableMultiSort: true,
      disableSortRemove: true,
      disableCreateExpandButton: true,
      sortTypes: {
        alphanumeric: (row1, row2, columnName) => {
          const rowOneColumn = row1.values[columnName];
          const rowTwoColumn = row2.values[columnName];
          if (rowOneColumn && Number.isNaN(Number(rowOneColumn))) {
            return rowOneColumn.toUpperCase() > rowTwoColumn.toUpperCase()
              ? 1
              : -1;
          }
          return Number(rowOneColumn) > Number(rowTwoColumn) ? 1 : -1;
        },
      },
    },
    ...plugins
  );

  useEffect(() => {
    setPage?.(pageIndex);
  }, [pageIndex, setPage]);

  useEffect(() => {
    setSize?.(pageSize);
  }, [pageSize, setSize]);

  useEffect(() => {
    setSortBy?.(sortBy);
    setPage?.(0);
  }, [sortBy, setSortBy, setPage]);

  useEffect(() => {
    setGlobalFilter(globalFilterValues);
  }, [globalFilterValues, data, setGlobalFilter]);

  useEffect(() => {
    setFilter("group", groupsKeys);
  }, [groupsKeys, data, setFilter]);

  useEffect(() => {
    setSelectedRowIds?.(selectedRowIds);
  }, [selectedRowIds, setSelectedRowIds]);

  const visibleRows = page ?? rows;

  const EmptyRow = useCallback(
    () => (
      <HvTableRow>
        <HvTableCell
          className={classes.noData}
          // colSpan is missing in the TS definition
          {...{ colSpan: headers.length }}
        >
          <HvEmptyState
            message={t("simple_table.noDataMessage")}
            icon={<Ban role="presentation" />}
          />
        </HvTableCell>
      </HvTableRow>
    ),
    [classes.noData, headers.length, t]
  );
  const bulkActionProps = getHvBulkActionsProps?.();

  return (
    <LoadingContainer hidden={!loading}>
      <HvBulkActions
        selectAllLabel={t("simple_table.all")}
        selectAllPagesLabel={t("memberships:members.labels.selectAcrossPages", {
          numTotal: bulkActionProps.numTotal,
        })}
        {...bulkActionProps}
        onSelectAll={(event) => {
          bulkActionProps.onSelectAll();
          onSelectAll?.(event, visibleRows, isAllPageRowsSelected);
        }}
        onSelectAllPages={(event) => {
          bulkActionProps.onSelectAllPages();
          onSelectAllPages?.(event, rows, toggleAllRowsSelected);
        }}
        actions={bulkActions}
      />
      <HvTableContainer style={{ overflow: "visible" }}>
        <HvTable {...getTableProps()}>
          <HvTableHead>
            <HvTableRow>
              {headers.map((col) => (
                <HvTableHeader {...col.getHeaderProps()}>
                  <OverflowTooltip
                    data={nodeFallback(col.render("Header"))}
                    paragraphOverflow
                  />
                </HvTableHeader>
              ))}
            </HvTableRow>
          </HvTableHead>
          <HvTableBody {...getTableBodyProps()}>
            {visibleRows.length > 0 ? (
              visibleRows
                // .filter((row) => !(viewMode === "member" && !row.isSelected))
                .map((row) => {
                  prepareRow(row);

                  const { key, ...rowProps } = row.getRowProps();

                  return (
                    <React.Fragment key={key}>
                      <HvTableRow
                        className={clsx({
                          [classes.rowIsAsignee]: row.original.assignee,
                        })}
                        onChange={(event) => onChangeRow?.(event, row)}
                        {...rowProps}
                        style={{ height: 40 }}
                      >
                        {row.cells.map((cell) => (
                          <HvTableCell
                            className={clsx({
                              [classes.cellIsAsignee]: row.original.assignee,
                            })}
                            {...cell.getCellProps()}
                          >
                            {cell.render("Cell")}
                          </HvTableCell>
                        ))}
                      </HvTableRow>
                    </React.Fragment>
                  );
                })
            ) : (
              <EmptyRow />
            )}
          </HvTableBody>
        </HvTable>
      </HvTableContainer>
      {rows.length > 10 && (
        <HvPagination {...getHvPaginationProps()} labels={paginationLabels} />
      )}
    </LoadingContainer>
  );
};

export default UserTable;
